import _ from "lodash"
import { ImmutableBehaviorSubject as BehaviorSubject } from "data/lib/immutable-behavior-subject"
import uuid from "uuid/v4"

import Client from "data/_client"
import { ApiResult } from "data/interface/api"
import { handleReferences } from 'data/lib/handle-references'
import incrementUnique from 'data/lib/increment-unique'
import { alphanum } from "components/table/lib/sort"
import notificationService from "data/notification"
import { PromiseWatch } from "./lib/promise-helpers"

// private module logic //

const id: string = "permission"
const initialState: ApiResult<object[]> = { id, state: "init", value: [] }
const permissionStream = new BehaviorSubject<ApiResult<object[]>>(initialState)
const permissionEventStream = new BehaviorSubject<{ type: string; payload: any }>({
  type: '',
  payload: {}
})
const _patches_ = PromiseWatch()
const _fetches_ = PromiseWatch()

type PermissionGroup = {
  correlationid?: string
  folderid?: number
  permissiontype?: "user-default" | "folder-permission" | "portfolio-reporting-default"
  role?: "owner" | "readonly" | "readwrite"
  users?: {
    type: "user" | "usergroup"
    ids: any[]
  }
  objects?: {
    type: "folder" | "property" | "portfolio"
    ids: number[]
  }
}

const current = () => permissionStream.getValue()

function clear(): void {
  permissionStream.next({ id, state: 'ok', value: [] })
}

function getFolderUserPermissions(folderid: number, userid: string): Promise<{owner: boolean, readwrite: boolean, readonly: boolean} | { message: string }> {
  return Client.get(`permission/folder/${folderid}/${userid}`)
  .then((permission: {owner: boolean, readwrite: boolean, readonly: boolean}) => {
    return permission
  })
  .catch((err: { message: string }) => {
    notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
    return err
  })
}

function fetch(folderid: number): Promise<object[] | { message: string }> {
  clear()

  return Client.get(`permission/folder/${folderid}`)
  .then((records: object[]) => {
    permissionStream.next({ id, state: 'ok', value: records })
    return records
  })
  .catch((err: { message: string }) => {
    notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
    return err
  })
}

function create(p: PermissionGroup): Promise<object | { message: string }> {
  const streamVal: object[] = permissionStream.getValue().value
  permissionStream.next({ id, state: 'busy', value: streamVal })

  let payload = p
  payload.correlationid = p.correlationid || uuid()
  if(!payload.permissiontype) payload.permissiontype = "folder-permission"

  return Client.post(`permission/folder`, payload)
    .then((permission: object) => {
      // merge results unique by permissionid
      const streamVal: object[] = permissionStream.getValue().value
      permissionStream.next({
        id,
        state: 'ok',
        value: _.union(permission, streamVal)
      })
      permissionEventStream.next({ type: 'USERPERMISSION_CREATED', payload: permission })
      return permission
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      permissionStream.next({ id, state: 'error', error: err, value: streamVal })
      notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
      return err
    })
}

async function patch(pg: PermissionGroup): Promise<boolean | { message: string }> {
  const old: object[] = permissionStream.getValue().value
  const streamVal = _.cloneDeep(old)

  streamVal.splice(_.findIndex(streamVal, a => a.correlationid === pg.correlationid), 1, pg)

  // optimistic update
  permissionStream.next({
    id,
    state: 'ok',
    value: streamVal
  })

  return Client.patch(`permission/folder`, pg)
    .then((permissions: PermissionGroup) => {
      return permissions[0]
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      permissionStream.next({ id, state: 'error', error: err, value: old })
      notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
      return err
    })
}

async function del(pg: PermissionGroup): Promise<boolean | { message: string }> {
  const streamVal: object[] = permissionStream.getValue().value

  // if(_.filter(streamVal, (g) => g.role === "owner").length === 1 && pg.role === "owner") {
  //   notificationService.notify({
  //     type: 'danger',
  //     message: "There must be at least one owner assigned to this folder",
  //     dismiss: 5000
  //   })
  //   return Promise.resolve(true)
  // }

  // filter out the deleted correlationid
  const value = _(streamVal)
    .filter((p) => p.correlationid !== pg.correlationid)
    .value()

  permissionStream.next({ id, state: 'ok', value })

  // update the db
  return Client.del(`permission/folder`, pg)
    .then((permissions: object) => {
      // update already happened optimistically
      return true
    })
    .catch((error: { message: string }) => {
      // on error overwrite optimistic update
      permissionStream.next({ id, state: 'error', error, value: streamVal })
      notificationService.notify({
        type: 'danger',
        message: error.message,
        dismiss: 5000
      })
      return error
    })
}

// public module interface //

const folderPermissionService = {
  stream: () => permissionStream.asObservable(),
  value: () => permissionStream.getValue().value,
  eventStream: () => permissionEventStream.asObservable(),
  getFolderUserPermissions,
  clear,
  create,
  del,
  fetch,
  patch
}

export default folderPermissionService
